/**
 * Derived progress numbers for an open board (Boards Redesign list 3,
 * cards 3.1-3.3, 12.10.2026). Pure functions over the assembled board, no
 * store access, so the Kanban column header, the List view group header
 * (list 6) and the board summary pill all read the same numbers.
 *
 * Counts are over EVERY task of the group/board: the "Show completed tasks"
 * toggle and the filter bar hide cards, they never change the progress (the
 * total always includes done tasks, card 3.1).
 */

function emptyProgress() {
  return { total: 0, done: 0, open: 0, pct: 0 };
}

function tally(tasks) {
  const out = emptyProgress();
  for (const t of tasks) {
    out.total += 1;
    if (t.done) out.done += 1;
  }
  out.open = out.total - out.done;
  // Floor, not round: 99.6 % must not read "100 %" while a task is still open.
  out.pct = out.total ? Math.floor((out.done / out.total) * 100) : 0;
  return out;
}

/**
 * Does this board show the progress bar and "N open" under each list name?
 * The per-board "Progress counts in column headers" switch (extras §2.4):
 * on unless explicitly false, which is the look every board had before the
 * switch existed. The done/total pill is not governed by it.
 */
export function showsColumnProgress(board) {
  return board?.showColumnProgress !== false;
}

/** { total, done, open, pct } for one list (group) of `board`. */
export function groupProgress(board, groupId) {
  if (!board || !Array.isArray(board.tasks)) return emptyProgress();
  return tally(board.tasks.filter((t) => t.groupId === groupId));
}

/** { total, done, open, pct } for the whole board. Tasks whose list no longer
 *  exists are not rendered anywhere, so they do not count either. */
export function boardProgress(board) {
  if (!board || !Array.isArray(board.tasks)) return emptyProgress();
  const groupIds = new Set((board.groups || []).map((g) => g.id));
  return tally(board.tasks.filter((t) => groupIds.has(t.groupId)));
}

/** Number of badge tints the list headers cycle through (pink, violet, blue,
 *  orange, green — the artboard order). */
export const LIST_BADGE_TONES = 5;

/** Tint slot (0..4) for the list at zero-based `index`. */
export function listBadgeTone(index) {
  const i = Number.isFinite(index) && index >= 0 ? Math.floor(index) : 0;
  return i % LIST_BADGE_TONES;
}

/** "just now", "5 min ago", "3 h ago", "4 d ago", else a short date. */
export function formatUpdatedAgo(iso, now = new Date()) {
  const then = iso ? new Date(iso) : null;
  if (!then || Number.isNaN(then.getTime())) return '';
  const sec = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} d ago`;
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
